import React from 'react';
import {
  Bookmark, Clock, Library, MessageCircle, Paintbrush, PanelLeft, PanelRight, PenLine, Search, Smartphone,
  Sparkles, Type, Wand2,
} from 'lucide-react';
import { AI_PANE, PANE_LABEL, PANE_ORDER, type DataPaneId, type PaneId } from './paneLayout';
import { useEditorCommands } from './editorCommands';

/**
 * 右侧图标栏：**只有图标**的窄竖条，固定在窗口最右侧。
 *
 * ## 高亮与去处（第六版模型：两个槽位）
 *
 * - **AI 创作助手**（`aiOpen`）：窗口内的**左**槽。它必须在主窗口里 ——
 *   吸附窗口是**独立的 SPA 实例**，拿不到这里正在跑的那个回合，会出现"两个 AI 面板各说各话"。
 * - **三个数据页**（`dataPane`）：窗口内的**右**槽（网页端），或**吸附窗口**（桌面端）。
 *   点另一页是**原位换内容**（不重放动画），点当前那页则收起。
 *
 * 两个槽位可以同时点亮：左边 AI、右边资料库。而且 **AI 永远在数据页左侧** ——
 * 这条是用户明确要求的（"打开 AI 助手，从已打开的功能左侧继续向左伸出"）。
 */
const PANE_ICON: Record<PaneId, React.ReactNode> = {
  settings: <PenLine size={18} />,
  data: <Library size={18} />,
  threads: <Bookmark size={18} />,
  resident: <MessageCircle size={18} />,
  ai: <Sparkles size={18} />,
};

export function PaneRail({
  aiOpen, dataPane, dataInWindow, satelliteOnLeft, onToggle, onSwapSide,
}: {
  /** AI 槽位是否展开（在数据页左侧） */
  aiOpen: boolean;
  /** 当前打开的数据页（没开则为 null） */
  dataPane: DataPaneId | null;
  /** 数据页是否渲染在窗口内（网页端 true；桌面端 false —— 它在吸附窗口里） */
  dataInWindow: boolean;
  /** 吸附窗口是否在主窗口左侧 */
  satelliteOnLeft: boolean;
  /** 点某一页：由调用方按上面的规则分派（AI 走窗口内左槽，数据页走右槽或吸附窗口） */
  onToggle: (id: PaneId) => void;
  /**
   * 列表顶部的**切换布局**按钮：把吸附窗口在"主窗口右侧 ↔ 左侧"之间挪。
   * 网页端不传（没有窗口系统）。
   */
  onSwapSide?: () => void;
}) {
  /**
   * 编辑器工具按钮的命令（编辑器挂载时才有）。
   *
   * ⚠️ 这个 hook 必须在**任何提前 return 之前**调用 —— 组件目前没有提前 return，
   * 以后加分支时留意 Hooks 规则。
   */
  const commands = useEditorCommands();

  return (
    <nav className="pane-rail" aria-label="右侧面板">
      {onSwapSide && (
        <button
          type="button"
          className="pane-rail-btn pane-rail-swap"
          aria-label={satelliteOnLeft ? '把功能页移到主窗口右侧' : '把功能页移到主窗口左侧'}
          title={satelliteOnLeft ? '切换布局：功能页移到主窗口右侧' : '切换布局：功能页移到主窗口左侧'}
          onClick={onSwapSide}
        >
          {satelliteOnLeft ? <PanelRight size={18} /> : <PanelLeft size={18} />}
        </button>
      )}

      {PANE_ORDER.map((id) => {
        const shown = id === AI_PANE ? aiOpen : dataPane === id;
        const where = dataInWindow ? '在窗口内展开' : '吸附在窗口旁边';
        return (
          <button
            key={id}
            type="button"
            className={`pane-rail-btn${shown ? ' is-active' : ''}`}
            // aria-pressed 表示"这一页现在看得见"
            aria-pressed={shown}
            aria-label={`${PANE_LABEL[id]}${shown ? '（已打开）' : ''}`}
            title={shown
              ? `${PANE_LABEL[id]} —— 点击收起`
              : id === AI_PANE
                ? `打开${PANE_LABEL[id]}（在数据页左侧展开）`
                : `打开${PANE_LABEL[id]}（${where}）`}
            onClick={() => onToggle(id)}
          >
            {PANE_ICON[id]}
          </button>
        );
      })}

      {/*
        ── 横杠以下：**编辑器工具**（原来在创作区工具栏右侧） ──
        用户要求把这批按钮搬到右侧栏，并用一根横杠与上面的"功能页"区分开。
        命令由编辑器发布（见 `editorCommands.ts`）—— 编辑器没挂载时（例如书架页）
        这一段整段不渲染。放在 spacer **之前**：两组紧挨着，靠横杠分隔。
      */}
      {commands && (
        <>
          <div className="pane-rail-divider" role="separator" aria-hidden="true" />

          <button
            type="button"
            className={`pane-rail-btn${commands.appearanceOpen ? ' is-active' : ''}`}
            aria-pressed={commands.appearanceOpen}
            title="编辑区外观：背景 / 稿纸行线 / 段落间距 / 首行缩进"
            onClick={commands.toggleAppearance}
          >
            <Paintbrush size={18} />
          </button>

          <button
            type="button"
            className="pane-rail-btn"
            title="取名工具：人物/地点/势力/物品名字生成"
            onClick={commands.openNameGenerator}
          >
            <Wand2 size={18} />
          </button>

          <button
            type="button"
            className={`pane-rail-btn${commands.findOpen ? ' is-active' : ''}`}
            aria-pressed={commands.findOpen}
            title="查找替换（Ctrl+F；全书检索 Ctrl+Shift+F）"
            onClick={commands.toggleFind}
          >
            <Search size={18} />
          </button>

          <button
            type="button"
            className="pane-rail-btn"
            title="预览：以手机（iPhone）屏幕查看当前章节阅读效果"
            onClick={commands.openPreview}
          >
            <Smartphone size={18} />
          </button>

          {commands.historyAvailable && (
            <button
              type="button"
              className="pane-rail-btn"
              title="历史版本"
              onClick={commands.openVersionHistory}
            >
              <Clock size={18} />
            </button>
          )}

          <button
            type="button"
            className={`pane-rail-btn${commands.summaryOpen ? ' is-active' : ''}`}
            aria-pressed={commands.summaryOpen}
            title="章节摘要"
            onClick={commands.toggleSummary}
          >
            <Type size={18} />
          </button>
        </>
      )}

      {/* 把下方留白推到底（图标栏下方的空白区） */}
      <div className="pane-rail-spacer" />
    </nav>
  );
}
